import { MouseEvent, ReactNode } from "react"
import { Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { StandardButton } from "../../../../../../../../shared/components/buttons/StandardButton"
import { StandardChip } from "../../../../../../../../shared/components/badges/StandardChip"
import { tailwindPalette } from "../../../../../../../theme/tailwindPalette"
import { LocalizedStrict } from "../../../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../../localization/components/AppLocalizationProvider"

type StyleProps = Readonly<{
    isEnabled: boolean
}>

const useStyles = makeStyles<StyleProps>()((theme, { isEnabled }) => ({
    card: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: ".75rem",
        height: "100%",
        padding: "1rem",
        cursor: "pointer",
        borderRadius: ".75rem",
        border: `1px solid ${isEnabled ? tailwindPalette.sky[400] : tailwindPalette.slate[200]}`,
        backgroundColor: isEnabled ? tailwindPalette.sky[50] : theme.palette.common.white,
        transition: "border-color .15s ease, background-color .15s ease",
        "&:hover": {
            borderColor: isEnabled ? tailwindPalette.sky[500] : tailwindPalette.slate[400],
        },
    },
    image: {
        width: "8rem",
        height: "8rem",
        objectFit: "contain",
    },
    title: {
        fontWeight: 600,
        textAlign: "center",
        color: tailwindPalette.slate[700],
    },
    footer: {
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
        // Reserve space for the tallest footer (chip + "Make standard" button) so toggling a
        // card's state never changes its height (and therefore the whole grid row's height).
        minHeight: "4.5rem",
        alignItems: "center",
        justifyContent: "flex-start",
    },
    statusChip: {
        height: "1.75rem",
        "& .MuiChip-label": {
            paddingLeft: ".75rem",
            paddingRight: ".75rem",
        },
    },
}))

type TableArrangementCardProps = Readonly<{
    title: ReactNode
    imageSrc: string
    imageAlt: string
    isEnabled: boolean
    isStandard: boolean
    onToggle: () => void
    onMakeStandard: () => void
}>

export function TableArrangementCard({
    title,
    imageSrc,
    imageAlt,
    isEnabled,
    isStandard,
    onToggle,
    onMakeStandard,
}: TableArrangementCardProps) {
    const { classes } = useStyles({ isEnabled })
    const { l10n } = useAppLocalization()

    const handleMakeStandard = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        onMakeStandard()
    }

    return (
        <div className={classes.card} onClick={onToggle}>
            <img src={imageSrc} alt={imageAlt} className={classes.image} />

            <Typography variant="h6" className={classes.title}>
                {title}
            </Typography>

            <div className={classes.footer}>
                {isStandard ? (
                    <StandardChip
                        variant="success"
                        placement="standalone"
                        size="small"
                        className={classes.statusChip}
                        label={l10n.getString("tab-table-arrangements-standard")}
                    />
                ) : isEnabled ? (
                    <>
                        <StandardChip
                            variant="grey"
                            placement="standalone"
                            size="small"
                            className={classes.statusChip}
                            label={l10n.getString("tab-table-arrangements-optional-chip")}
                        />
                        <LocalizedStrict id="tab-table-arrangements-make-standard-button">
                            <StandardButton
                                variant="outlined"
                                color="grey"
                                size="small"
                                fullWidth={false}
                                onClick={handleMakeStandard}
                            >
                                Make standard
                            </StandardButton>
                        </LocalizedStrict>
                    </>
                ) : null}
            </div>
        </div>
    )
}

